<template>
    <article
        :id="unit.id"
        class="g10c-unit"
        v-show="unitVisible"
    >
        <button
            type="button"
            class="g10c-unit__header"
            :aria-expanded="String(isOpen)"
            :aria-controls="`${unit.id}-body`"
            @click="toggle"
        >
            <span class="g10c-unit__num" aria-hidden="true">{{ String(unit.number).padStart(2, '0') }}</span>
            <span class="g10c-unit__icon" aria-hidden="true">
                <lucide-icon :name="unit.icon" :size="22" />
            </span>
            <span class="g10c-unit__titles">
                <span class="g10c-unit__title">{{ unit.arabicTitle }}</span>
                <span class="g10c-unit__en ltr-inline">{{ unit.englishTitle }}</span>
            </span>
            <span
                v-if="primaryStatus"
                class="g10c-status"
                :class="`g10c-status--${primaryStatus}`"
            >
                {{ statusLabel(primaryStatus) }}
            </span>
            <lucide-icon
                name="ChevronDown"
                :size="20"
                class="g10c-unit__chevron"
                :class="{ 'g10c-unit__chevron--open': isOpen }"
                aria-hidden="true"
            />
        </button>

        <div :id="`${unit.id}-body`" class="g10c-unit__body" v-show="isOpen">
            <p class="g10c-unit__summary">{{ unit.summary }}</p>

            <div v-if="unit.concepts && unit.concepts.length" class="g10c-concepts">
                <p class="g10c-concepts__label">مفاهيم مهمة</p>
                <ul class="g10c-concepts__list">
                    <li v-for="concept in unit.concepts" :key="`${unit.id}-${concept}`" class="ltr-inline">
                        {{ concept }}
                    </li>
                </ul>
            </div>

            <aside v-if="unit.studentOutcome" class="g10c-core-idea">
                <p class="g10c-core-idea__label">ماذا يخرج به الطالب؟</p>
                <p class="g10c-core-idea__text">{{ unit.studentOutcome }}</p>
            </aside>

            <p v-if="unit.realLife" class="g10c-unit__real">
                <strong>مثال من الحياة:</strong> {{ unit.realLife }}
            </p>

            <div class="g10c-lessons">
                <p class="g10c-lessons__label">دروس الوحدة</p>
                <ul class="g10c-lessons__list">
                    <li
                        v-for="lesson in unit.lessons"
                        :key="`${unit.id}-${lesson.code}`"
                        class="g10c-lesson"
                        v-show="lessonVisible(lesson)"
                    >
                        <div class="g10c-lesson__main">
                            <span class="g10c-lesson__code ltr-inline">{{ lesson.code }}</span>
                            <span class="g10c-lesson__titles">
                                <span class="g10c-lesson__ar">{{ lesson.arabicTitle }}</span>
                                <span class="g10c-lesson__en ltr-inline">{{ lesson.englishTitle }}</span>
                            </span>
                        </div>
                        <div class="g10c-lesson__meta">
                            <span
                                class="g10c-status g10c-status--sm"
                                :class="`g10c-status--${lesson.status}`"
                            >
                                {{ statusLabel(lesson.status) }}
                            </span>
                            <span class="g10c-type">{{ typeLabel(lesson.type) }}</span>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    </article>
</template>

<script>
import LucideIcon from '@/components/welcome/landing/LucideIcon.vue';
import { STATUS_LABELS, TYPE_LABELS } from './grade10-curriculum-data';

export default {
    name: 'CurriculumUnitBlock',
    components: { LucideIcon },
    props: {
        unit: { type: Object, required: true },
        unitVisible: { type: Boolean, default: true },
        lessonMatcher: { type: Function, default: null },
        initiallyOpen: { type: Boolean, default: false },
    },
    data() {
        return {
            isOpen: this.initiallyOpen,
        };
    },
    computed: {
        primaryStatus() {
            const statuses = [...new Set((this.unit.lessons || []).map((l) => l.status))];
            if (statuses.length === 1) return statuses[0];
            if (statuses.includes('term-1') && !statuses.includes('term-2')) return 'term-1';
            if (statuses.includes('term-2') && !statuses.includes('term-1')) return 'term-2';
            if (statuses.every((s) => s === 'not-in-distribution')) return 'not-in-distribution';
            return null;
        },
    },
    methods: {
        statusLabel(status) {
            return STATUS_LABELS[status] || status;
        },
        typeLabel(type) {
            return TYPE_LABELS[type] || type;
        },
        lessonVisible(lesson) {
            if (typeof this.lessonMatcher !== 'function') return true;
            return this.lessonMatcher(lesson);
        },
        toggle() {
            this.isOpen = !this.isOpen;
            if (this.isOpen) {
                this.$emit('open', { unitId: this.unit.id, title: this.unit.arabicTitle });
            }
        },
    },
};
</script>
